import { NodeType, NODE_TYPE_TO_PVF } from '@/app/components/Editor/Plugins/Permissions/utils'
import { NewsletterContent, Paths } from '../../types'
import { GROUP_PVF_MAP } from '../../constants'
import pvfs from './pvfs'

class ContentPermissionsManager {
  protected pvfs: string[]

  constructor(pvfs: string[] = []) {
    this.pvfs = pvfs
  }

  getUserContentGroups(): string[] {
    const groups = this.pvfs.filter((pvf) => pvf in GROUP_PVF_MAP).map((pvf) => GROUP_PVF_MAP[pvf])
    return groups.length > 0 ? groups : ['news']
  }

  canAccessContent(contentGroups: string[]): boolean {
    if (this.isAdmin()) return true
    const userGroups = this.getUserContentGroups()
    return userGroups.some((group) => contentGroups.includes(group))
  }

  scope(where: any) {
    if (this.isAdmin()) return where

    const userGroups = this.getUserContentGroups()

    const groupCondition = {
      content_groups: { hasSome: userGroups },
    }

    if (where.AND) {
      return { AND: [...where.AND, groupCondition] }
    }

    return { AND: [where, groupCondition] }
  }

  canCreate() {
    return this.isAdmin() || this.isCreator()
  }

  canEdit() {
    return this.isAdmin() || this.isEditor()
  }

  canManageAds() {
    return this.isAdmin() || this.isAdManager()
  }

  canDelete() {
    return this.isAdmin() || this.isCreator()
  }

  canCreateNode(nodeType: NodeType) {
    const pvf = NODE_TYPE_TO_PVF[nodeType]?.create
    if (!pvf) {
      console.warn(`No PVF mapped for node type: ${nodeType}`)
    }
    return this.isAdmin() || this.pvfs.includes(pvf)
  }

  canDeleteNode(nodeType: NodeType) {
    const pvf = NODE_TYPE_TO_PVF[nodeType]?.delete
    if (!pvf) {
      console.warn(`No PVF mapped for node type: ${nodeType}`)
    }
    return this.isAdmin() || this.pvfs.includes(pvf)
  }

  canEditNode(nodeType: NodeType) {
    const pvf = NODE_TYPE_TO_PVF[nodeType]?.edit
    if (!pvf) {
      console.warn(`No PVF mapped for node type: ${nodeType}`)
    }
    return this.isAdmin() || this.pvfs.includes(pvf)
  }

  canEditField(field: Paths<NewsletterContent>) {
    if (this.isAdmin()) {
      return true
    }

    if (['publishToWeb', 'publishToTerminal'].includes(field)) {
      return this.isPublisher()
    }

    if (['emailList', 'testEmailList', 'fromEmailName', 'content_groups'].includes(field)) {
      return this.pvfs.includes(pvfs.TEMPLATE_EDIT)
    }

    if (field.includes('viewership')) {
      return this.pvfs.includes(pvfs.TEMPLATE_EDIT)
    }

    if (field.startsWith('adOptions') || field === 'adTestEmailList') {
      return this.isAdManager()
    }

    return this.canEdit()
  }

  canRead() {
    return this.isAdmin() || this.isReader()
  }

  canPublish() {
    return this.isAdmin() || this.isPublisher()
  }

  isAdmin() {
    const stagingAdmin = (process.env.NODE_ENV as string) && this.pvfs.includes(pvfs.ADMIN_STAGING)
    return this.pvfs.includes(pvfs.ADMIN) || !!stagingAdmin
  }

  isReader() {
    return this.pvfs.includes(pvfs.CAMPAIGN_VIEW)
  }

  isCreator() {
    return this.pvfs.includes(pvfs.CAMPAIGN_CREATE)
  }

  isEditor() {
    return this.pvfs.includes(pvfs.CAMPAIGN_EDIT)
  }

  isPublisher() {
    return this.pvfs.includes(pvfs.CAMPAIGN_PUBLISH)
  }

  isAdManager() {
    return this.pvfs.includes(pvfs.AD_EDIT)
  }
}

export default ContentPermissionsManager
